import {
  DB_VERSION,
  deleteStudentLearningByStudentId,
  getData,
  getModelEntry,
  getNextWords,
  openDB,
  saveData,
  storeWords,
  studentLearningProgress,
  submit,
} from "./_db";
import { fetchAndParseCsv } from "./_fileToData";
import type { Submission, StudentRecord, Word } from "./backendTypes";

type AttemptStatus = "not attempted" | "correct" | "wrong" | "skipped";
type AttemptStatusLegacy = AttemptStatus | 0 | 1 | 2 | 3;

type StudentWordAttempt = {
  spoken_en: AttemptStatusLegacy;
  drawn: AttemptStatusLegacy;
  spoken_te: AttemptStatusLegacy;
  date: string;
  count: number;
};

type StudentWordLearning = {
  wordEnglish: string;
  attempts: StudentWordAttempt[];
};

export type CashierGameSession = {
  date: string;
  score: number;
  questionsCorrect: number;
  questionsTotal: number;
  hintsUsed: number;
  bestStreak: number;
};

export type CashierGameProgress = {
  totalScore: number;
  highScore: number;
  bestStreak: number;
  roundsPlayed: number;
  sessions: CashierGameSession[];
};

type StudentLearningRecord = {
  studentId: number;
  words: StudentWordLearning[];
  toyProgress?: any;
  collectible?: any;
  cashierGame?: CashierGameProgress;
};

type DashboardLearningRow = {
  id: string;
  studentId: number;
  wordEnglish: string;
  difficulty: "H" | "M" | "E";
  type: Submission["type"];
  skip: boolean;
  correct: boolean;
  timestamp: string;
};

export type TeacherStudentOverview = {
  id: number;
  name: string;
  rollNo: string;
  class: number;
  section: SectionName;
  englishCorrect: number;
  teluguCorrect: number;
  drawnCorrect: number;
  easy: number;
  medium: number;
  hard: number;
  pronunciationCorrect: number;
  pronunciationAttempted: number;
  wordsShown: number;
  wordsAttempted: number;
  wordsSkipped: number;
  wordsSpoken: number;
  wordsLearned: number;
  wordsDrawn: number;
  lastPractice: string;
};

export type WordTaskProgressState = "pending" | "correct" | "wrong";

export type WordTaskStatus = {
  english: WordTaskProgressState;
  telugu: WordTaskProgressState;
  drawing: WordTaskProgressState;
};

export type TeacherSkipRequest = {
  id: string;
  studentId: number;
  classId: number;
  studentName: string;
  wordEnglish: string;
  wordLocal: string;
  category: string;
  level?: string;
  status: "pending" | "approved" | "denied";
  timestamp: string;
  teacherNote: string | null;
};

export type TeacherWrongSubmission = {
  id: string;
  studentId: number;
  classId: number;
  studentName: string;
  section: string;
  wordEnglish: string;
  wordLocal: string;
  category: string;
  level?: string;
  attemptType: "pronunciation" | "drawing";
  timestamp: string;
};

export type TeacherDashboardData = {
  stats: {
    totalStudents: number;
    wordsSpoken: number;
    wordsLearned: number;
  };
  students: TeacherStudentOverview[];
  skipRequests: TeacherSkipRequest[];
  wrongSubmissions: TeacherWrongSubmission[];
};

export type LeaderboardEntry = {
  id: number;
  name: string;
  class: number;
  collectibles: number;
  words: number;
  rank: number;
};

export type LeaderboardData = {
  entries: LeaderboardEntry[];
};

type CachedModelEntry = {
  modelUse: string;
  modelFile?: Blob;
  chunked?: boolean;
  totalChunks?: number;
};

const IMAGE_MODEL_URL =
  "https://storage.googleapis.com/vocab-assets/models/mobilenet_sketch_classifier.onnx";
const IMAGE_MODEL_DATA_URL =
  "https://storage.googleapis.com/vocab-assets/models/mobilenet_sketch_classifier.onnx.data";

const REQUIRED_AUDIO_MODEL_FILES = [
  "config.json",
  "generation_config.json",
  "preprocessor_config.json",
  "tokenizer_config.json",
  "tokenizer.json",
  "decoder_model_merged_quantized.onnx",
  "encoder_model_quantized.onnx",
] as const;

const AUDIO_MODEL_BASE_URL = "https://storage.googleapis.com/vocab-assets/models";

const OLD_QWEN_ONNX_MODEL_PREFIX = "qwen2.5-1.5b-onnx-int4:";
const QWEN_LARGE_FILE_CACHE_FAILED_AT_KEY = "qwenLargeModelCacheFailedAt";
const TELUGU_AUDIO_MODEL_URL = "https://storage.googleapis.com/vocab-assets/models/asr_int8_int4.onnx";
const TELUGU_FEATURE_MAP_URL = "https://storage.googleapis.com/vocab-assets/models/Telugu-Map.csv";

const cacheImageModel = async (): Promise<void> => {
  const imageModelUrl = await fetch(IMAGE_MODEL_URL);
  if (!imageModelUrl.ok) {
    throw new Error(`Failed to download image model: ${imageModelUrl.statusText}`);
  }

  const imageModelFile = await imageModelUrl.blob();

  const dataUrl = await fetch(IMAGE_MODEL_DATA_URL);
  if (!dataUrl.ok) {
    throw new Error(
      `Failed to download image model data: ${dataUrl.statusText}`
    );
  }

  const dataFile = await dataUrl.blob();
  await saveData(
    {
      modelUse: "image",
      modelName: "sketch_model.onnx",
      modelFile: imageModelFile,
      dataFile,
    },
    "model"
  );
};

const isOpfsAvailable = (): boolean => {
  return typeof navigator !== "undefined" && Boolean(navigator.storage?.getDirectory);
};

const cacheTeluguModel = async (): Promise<void> => {
  console.log("🔵 cacheTeluguModel called");

  const modelRes = await fetch(TELUGU_AUDIO_MODEL_URL);
  console.log("1️⃣ model fetch done:", modelRes.status);
  if (!modelRes.ok) {
    throw new Error(`Failed to download Telugu model: ${modelRes.statusText}`);
  }
  const modelFile = await modelRes.blob();
  console.log("2️⃣ model blob done, size:", modelFile.size);

  const mapRes = await fetch(encodeURI(TELUGU_FEATURE_MAP_URL));
  console.log("3️⃣ map fetch done:", mapRes.status);
  if (!mapRes.ok) {
    throw new Error(`Failed to download Telugu feature map: ${mapRes.statusText}`);
  }
  const dataFile = await mapRes.blob();
  console.log("4️⃣ map blob done, size:", dataFile.size);

  await saveData(
    {
      modelUse: "telugu",
      modelName: "asr_int8_int4.onnx",
      modelFile,
      dataFile,
    },
    "model"
  );
  console.log("✅ Telugu model + map saved to IndexedDB");
};

const ensureRequiredModelCache = async (): Promise<void> => {
  const existingModels =
    ((await getData<CachedModelEntry[]>("model")) ?? []).map((entry) =>
      entry.modelUse
    );
  const existingSet = new Set(existingModels);

  // ── Image model (unchanged) ───────────────────────────────────────────────
  const imageModelEntry = await getModelEntry("image");
  const hasCompleteImageModel = Boolean(
    imageModelEntry?.modelFile && imageModelEntry.dataFile
  );
  if (!hasCompleteImageModel) {
    window.dispatchEvent(
      new CustomEvent("setup:progress", {
        detail: {
          message: "Caching drawing classifier model components...",
          progress: 10,
        },
      })
    );
    try {
      await cacheImageModel();
      console.log("Saved image model to DB.");
      window.dispatchEvent(
        new CustomEvent("setup:progress", {
          detail: {
            message: "Stored drawing classifier model components.",
            progress: 15,
          },
        })
      );
    } catch (error) {
      console.error("Failed to cache image model", error);
    }
  }

  // ── Telugu model (NEW) ────────────────────────────────────────────────────
  const teluguModelEntry = await getModelEntry("telugu");
  const hasCompleteTeluguModel = Boolean(
    teluguModelEntry?.modelFile && teluguModelEntry.dataFile
  );
  if (!hasCompleteTeluguModel) {
    try {
      await cacheTeluguModel();
      console.log("Saved Telugu model to DB.");
    } catch (error) {
      console.error("Failed to cache Telugu model", error);
    }
  }

  // ── Audio/Whisper files (unchanged) ───────────────────────────────────────
  const missingAudioFiles = REQUIRED_AUDIO_MODEL_FILES.filter(
    (fileName) => !existingSet.has(fileName)
  );

  if (missingAudioFiles.length > 0) {
    let completedAudioCount = 0;
    await Promise.all(
      missingAudioFiles.map(async (fileName) => {
        const url = `${AUDIO_MODEL_BASE_URL}/${fileName}`;
        console.log(`Downloading ${fileName}...`);

        try {
          const response = await fetch(url);
          if (!response.ok) {
            console.error(
              `❌ Failed to download ${fileName}: ${response.status} ${response.statusText}`
            );
            return;
          }

          const blob = await response.blob();
          await saveData({ modelUse: fileName, modelFile: blob }, "model");
          console.log(`✅ Stored ${fileName} in IndexedDB`);
          
          completedAudioCount++;
          const percent = Math.round((completedAudioCount / missingAudioFiles.length) * 100);
          // Scale audio model progress from 15% to 95% of total setup progress now that Qwen is removed
          const scaledProgress = 15 + Math.round((percent * 80) / 100);
          window.dispatchEvent(
            new CustomEvent("setup:progress", {
              detail: {
                message: `Caching audio pronunciation models (${completedAudioCount}/${missingAudioFiles.length})...`,
                progress: scaledProgress,
              },
            })
          );
        } catch (error) {
          console.error(`❌ Failed to cache ${fileName}:`, error);
        }
      })
    );
  }
};


const cleanupOldQwenOnnxCache = async (): Promise<void> => {
  const db = await openDB();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(["model"], "readwrite");
    const store = transaction.objectStore("model");
    let deletedCount = 0;

    transaction.oncomplete = () => {
      if (deletedCount > 0) {
        console.info("Removed old Qwen ONNX IndexedDB cache entries.", {
          prefix: OLD_QWEN_ONNX_MODEL_PREFIX,
          deletedCount,
        });
      }
      resolve();
    };
    transaction.onerror = () => {
      reject(transaction.error ?? new Error("Failed to clean old Qwen ONNX cache."));
    };

    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;

      const key =
        typeof cursor.key === "string"
          ? cursor.key
          : String((cursor.value as CachedModelEntry | undefined)?.modelUse ?? "");

      if (key.startsWith(OLD_QWEN_ONNX_MODEL_PREFIX)) {
        cursor.delete();
        deletedCount += 1;
      }

      cursor.continue();
    };
    request.onerror = () => {
      reject(request.error ?? new Error("Failed to scan model cache."));
    };
  });

  localStorage.removeItem(QWEN_LARGE_FILE_CACHE_FAILED_AT_KEY);
};

const toTimestampMs = (value?: number | string): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const formatDate = (timestamp: number | null): string => {
  if (!timestamp) return "-";
  return new Date(timestamp).toLocaleDateString();
};

const formatDateTime = (timestamp: number | null): string => {
  if (!timestamp) return "-";
  return new Date(timestamp).toLocaleString();
};

const normalizeAttemptStatus = (status: AttemptStatusLegacy): AttemptStatus => {
  if (typeof status === "string") return status;
  if (status === 1) return "correct";
  if (status === 2) return "wrong";
  if (status === 3) return "skipped";
  return "not attempted";
};

const isAttemptCompleted = (status: AttemptStatus): boolean =>
  status === "correct" || status === "wrong";

const toDashboardRow = (
  studentId: number,
  wordEnglish: string,
  date: string,
  index: number,
  type: Submission["type"],
  status: AttemptStatusLegacy,
  difficulty: "H" | "M" | "E"
): DashboardLearningRow | null => {
  const normalized =
    typeof status === "string"
      ? status
      : status === 1
      ? "correct"
      : status === 2
      ? "wrong"
      : status === 3
      ? "skipped"
      : "not attempted";

  if (normalized === "not attempted") return null;
  return {
    id: `${studentId}-${wordEnglish}-${date}-${type}-${index}`,
    studentId,
    wordEnglish,
    difficulty,
    type,
    skip: normalized === "skipped",
    correct: normalized === "correct",
    timestamp: date,
  };
};

const flattenLearningRows = (
  records: StudentLearningRecord[],
  wordMap: Map<string, Word>
): DashboardLearningRow[] => {
  const rows: DashboardLearningRow[] = [];

  for (const record of records) {
    for (const word of record.words) {
      const difficulty = wordMap.get(word.wordEnglish)?.difficulty ?? "E";

      word.attempts.forEach((attempt, idx) => {
        const candidateRows = [
          toDashboardRow(
            record.studentId,
            word.wordEnglish,
            attempt.date,
            idx,
            "audio_english",
            attempt.spoken_en,
            difficulty
          ),
          toDashboardRow(
            record.studentId,
            word.wordEnglish,
            attempt.date,
            idx,
            "audio_telugu",
            attempt.spoken_te,
            difficulty
          ),
          toDashboardRow(
            record.studentId,
            word.wordEnglish,
            attempt.date,
            idx,
            "image",
            attempt.drawn,
            difficulty
          ),
        ];

        for (const row of candidateRows) {
          if (row) rows.push(row);
        }
      });
    }
  }

  return rows;
};

export async function assessTeluguPronunciation(
  audioBlob: Blob,
  targetWord: string
) {
  const teluguEntry = await getModelEntry("telugu");
  const hasCompleteTeluguModel = Boolean(
    teluguEntry?.modelFile && teluguEntry?.dataFile
  );
  if (!hasCompleteTeluguModel) {
    await cacheTeluguModel();
  }
  const { assess } = await import('./transcriberTe.js');
  return await assess(audioBlob, targetWord);
}

const LOCAL_LANGUAGE_STORAGE_KEY = "preferredLocalLanguage";
const DEFAULT_LOCAL_LANGUAGE = "telugu";

const getPreferredLocalLanguage = (): string => {
  const value = localStorage.getItem(LOCAL_LANGUAGE_STORAGE_KEY)?.trim().toLowerCase();
  return value || DEFAULT_LOCAL_LANGUAGE;
};

const resolveWordLocalText = (word: Word | undefined, preferredLanguage: string): string => {
  if (!word) return "-";

  const rawWord = word as unknown as Record<string, unknown>;
  const normalizedLanguage = preferredLanguage.trim().toLowerCase();
  const normalizedAliases: Record<string, string> = {
    te: "telugu",
    ta: "tamil",
    hi: "hindi",
    mr: "marathi",
    marati: "marathi",
  };

  const language = normalizedAliases[normalizedLanguage] ?? normalizedLanguage;
  const directValue = rawWord[language];
  if (typeof directValue === "string" && directValue.trim().length > 0) {
    return directValue;
  }

  const translations = rawWord.translations;
  if (translations && typeof translations === "object") {
    const translationMap = translations as Record<string, unknown>;
    const requestedTranslation = translationMap[language];
    if (
      typeof requestedTranslation === "string" &&
      requestedTranslation.trim().length > 0
    ) {
      return requestedTranslation;
    }

    for (const value of Object.values(translationMap)) {
      if (typeof value === "string" && value.trim().length > 0) {
        return value;
      }
    }
  }

  const teluguFallback = rawWord[DEFAULT_LOCAL_LANGUAGE];
  if (typeof teluguFallback === "string" && teluguFallback.trim().length > 0) {
    return teluguFallback;
  }

  return "-";
};

// ── Hardcoded school / student / teacher data ──
// Only the active school's data is used at runtime; other schools are never
// written to IndexedDB, so they won't appear in DevTools.

export type School = { schoolId: number; schoolName: string };

export type HardcodedStudent = {
  /** Globally unique numeric id in the format SCCR (School, Class, Roll). */
  studentId: number;
  studentName: string;
  classId: number;  // 1-5
  section: SectionName;
  rollNo: string;
  schoolId: number;
  colour: string;
  emoji: string;
};

type SectionName = "A" | "B" | "C" | "D" | "E";
const SECTION_NAMES: SectionName[] = ["A", "B", "C", "D", "E"];

type ClassSectionMap = Record<
  number,
  {
    classId: number;
    sections: Record<SectionName, HardcodedStudent[]>;
  }
>;

export type HardcodedTeacher = {
  teacherId: number;
  teacherName: string;
  schoolId: number;
};

// Schools 1-4 share this 25-name pool and names are reused to build 100 students.
const STUDENT_NAMES = [
  "Aarav",   "Diya",    "Rohan",   "Priya",   "Vikram",
  "Sneha",   "Arjun",   "Ananya",  "Kiran",   "Meera",
  "Ravi",    "Pooja",   "Suresh",  "Divya",   "Aditya",
  "Kavya",   "Nikhil",  "Shreya",  "Rahul",   "Lakshmi",
  "Sai",     "Nisha",   "Tarun",   "Chitra",  "Mohan",
];

// School 5 uses a distinct 25-name pool and reuses it to build 100 students.
const STUDENT_NAMES_SCHOOL_5 = [
  "Aryan",   "Ishaan",  "Kabir",   "Mihir",   "Prateek",
  "Anushka", "Bhavna",  "Deepika", "Geeta",   "Hema",
  "Ishan",   "Jatin",   "Kartik",  "Lokesh",  "Manish",
  "Nalini",  "Omkar",   "Pallavi", "Qasim",   "Rekha",
  "Sachin",  "Tanvi",   "Uday",    "Varsha",  "Yogesh",
];

const COLOURS = ["bg-blue-400", "bg-green-400", "bg-pink-400", "bg-yellow-400", "bg-purple-400"];
const EMOJIS = [
  "/profile-icons/image-9.png", "/profile-icons/image-10.png",
  "/profile-icons/image-11.png", "/profile-icons/image-12.png",
  "/profile-icons/image-13.png",
];

export const SCHOOLS: School[] = [
  { schoolId: 1, schoolName: "Government High School, Raj Bhavan, Somajiguda, Hyderabad" },
  { schoolId: 2, schoolName: "Air Force School, Begumpet, Hyderabad" },
  { schoolId: 3, schoolName: "Govt. Ps Bhagath Singh, Warangal" },
  { schoolId: 4, schoolName: "Govt. Ps Auto Nagar, Warangal" },
  { schoolId: 5, schoolName: "Govt. Ps Bombay Rice Mill, Warangal" },
];

function buildSchoolData(school: School, studentNames: string[], teacherNames: string[]) {
  const buildStudentId = (
    schoolId: number,
    classId: number,
    sectionCode: number,
    rollNo: number
  ) => schoolId * 100000 + classId * 10000 + sectionCode * 1000 + rollNo;

  // 100 students: 5 classes × 5 sections × 4 students each.
  const STUDENTS_PER_SECTION = 4;
  const classMap: Partial<ClassSectionMap> = {};
  let nameIdx = 0;
  for (let classId = 1; classId <= 5; classId++) {
    const sectionStudentMap: Record<SectionName, HardcodedStudent[]> = {
      A: [],
      B: [],
      C: [],
      D: [],
      E: [],
    };

    for (let idx = 0; idx < SECTION_NAMES.length; idx++) {
      const section = SECTION_NAMES[idx];
      const sectionCode = idx + 1;
      for (let rollNo = 1; rollNo <= STUDENTS_PER_SECTION; rollNo++) {
        const baseName = studentNames[nameIdx % studentNames.length] ?? "Student";
        const student = {
          studentId: buildStudentId(school.schoolId, classId, sectionCode, rollNo),
          studentName: baseName,
          classId,
          section,
          rollNo: String(rollNo),
          schoolId: school.schoolId,
          colour: COLOURS[nameIdx % COLOURS.length],
          emoji: EMOJIS[nameIdx % EMOJIS.length],
        };

        sectionStudentMap[section].push(student);
        nameIdx++;
      }
    }

    classMap[classId] = {
      classId,
      sections: sectionStudentMap,
    };
  }

  const teachers: HardcodedTeacher[] = teacherNames.map((name, i) => ({
    teacherId: (school.schoolId - 1) * 2 + i + 1,
    teacherName: name,
    schoolId: school.schoolId,
  }));

  return {
    teachers,
    class: classMap as ClassSectionMap,
  };
}

// Per-school configuration; index must align with SCHOOLS.
// Each entry pairs a student name pool with 2 teacher names for that school.
const SCHOOL_CONFIGS: { studentNames: string[]; teacherNames: string[] }[] = [
  { studentNames: STUDENT_NAMES,          teacherNames: ["Ms. Radha",    "Mr. Venkat"]   },
  { studentNames: STUDENT_NAMES,          teacherNames: ["Ms. Parvathi", "Mr. Gopal"]    },
  { studentNames: STUDENT_NAMES,          teacherNames: ["Ms. Sunita",   "Mr. Ramesh"]   },
  { studentNames: STUDENT_NAMES,          teacherNames: ["Ms. Leela",    "Mr. Sunil"]    },
  { studentNames: STUDENT_NAMES_SCHOOL_5, teacherNames: ["Ms. Kamala",   "Mr. Krishnan"] },
];

const SCHOOL_DATA = SCHOOLS.map((school, i) => ({
  school,
  ...buildSchoolData(school, SCHOOL_CONFIGS[i].studentNames, SCHOOL_CONFIGS[i].teacherNames),
}));

// ── Active school (stored in localStorage + IndexedDB) ──

const ACTIVE_SCHOOL_KEY = "activeSchoolId";
const SCHOOL_STORE_BACKUP_KEY = "schoolStoreBackup:v1";

export function getActiveSchoolId(): number {
  return Number(localStorage.getItem(ACTIVE_SCHOOL_KEY) || "1");
}

/**
 * (Re-)seeds IndexedDB for the given school (or the currently active one).
 *
 * - Upserts the target `school` record with the latest hardcoded data, including
 *   embedded teachers and class->section->students mappings.
 */
export async function seedActiveSchoolToIndexedDB(schoolId?: number): Promise<void> {
  const id = schoolId ?? getActiveSchoolId();
  const entry = SCHOOL_DATA.find((d) => d.school.schoolId === id);
  if (!entry) return;

  await openDB();

  // Upsert only this school record so other schools and user data remain intact.
  await saveData({
    ...entry.school,
    teachers: entry.teachers,
    class: entry.class,
  }, "school");

  console.log(`Seeded IndexedDB with data for: ${entry.school.schoolName}`);
}

const backupSchoolStoreOnce = async (): Promise<void> => {
  if (localStorage.getItem(SCHOOL_STORE_BACKUP_KEY)) return;

  await openDB();
  const schools = (await getData<StoredSchool[]>("school")) ?? [];
  if (schools.length === 0) return;

  try {
    localStorage.setItem(
      SCHOOL_STORE_BACKUP_KEY,
      JSON.stringify({
        createdAt: new Date().toISOString(),
        schools,
      })
    );
  } catch (error) {
    console.warn("Could not create school backup snapshot", error);
  }
};

const ensureSchoolSeededIfMissing = async (schoolId: number): Promise<void> => {
  await openDB();
  const schools = (await getData<StoredSchool[]>("school")) ?? [];
  const hasSchool = schools.some((school) => school.schoolId === schoolId);

  if (!hasSchool) {
    await seedActiveSchoolToIndexedDB(schoolId);
  }
};

export async function setActiveSchoolId(schoolId: number): Promise<void> {
  localStorage.setItem(ACTIVE_SCHOOL_KEY, String(schoolId));
  await ensureSchoolSeededIfMissing(schoolId);
}

export function getActiveSchool(): School {
  const id = getActiveSchoolId();
  return SCHOOLS.find((s) => s.schoolId === id) ?? SCHOOLS[0];
}

export function getStudentsForActiveSchool(): HardcodedStudent[] {
  const id = getActiveSchoolId();
  const classMap = SCHOOL_DATA.find((d) => d.school.schoolId === id)?.class;
  if (!classMap) return [];

  return Object.values(classMap).flatMap((classEntry) =>
    Object.values(classEntry.sections).flatMap((sectionStudents) => sectionStudents)
  );
}

export function getTeachersForActiveSchool(): HardcodedTeacher[] {
  const id = getActiveSchoolId();
  return SCHOOL_DATA.find((d) => d.school.schoolId === id)?.teachers ?? [];
}

// ── Student lookup (hardcoded, no DB) ──

export function findStudent(
  classId: number,
  rollNo: string,
  section?: SectionName,
  studentName?: string
): HardcodedStudent | null {
  const students = getStudentsForActiveSchool();
  return (
    students.find(
      (s) =>
        s.classId === classId &&
        (!section || s.section === section) &&
        s.rollNo === rollNo &&
        (!studentName || s.studentName === studentName)
    ) ?? null
  );
}

// ── Word practice ──

// Resolves once initialSetup() has finished (or immediately if skipped).
let _setupReady: Promise<void> = Promise.resolve();
let _initialSetupInFlight: Promise<void> | null = null;

export async function waitForInitialSetup(): Promise<void> {
  await _setupReady;
}

export async function practiceWords(studentId: number) {
  await _setupReady;
  return await getNextWords(studentId);
}

export async function checkStudentAnswer(submission: Submission): Promise<boolean> {
  return await submit(submission);
}

export async function getStudentLearningProgress(studentId: number) {
  return await studentLearningProgress(studentId);
}

export async function getStudentWordTaskStatus(
  studentId: number,
  wordEnglish: string
): Promise<WordTaskStatus> {
  await openDB();

  const learningRecords = (await getData<StudentLearningRecord[]>("student_learning")) ?? [];
  const learningRecord = learningRecords.find((record) => record.studentId === studentId);
  const wordRecord = learningRecord?.words.find(
    (word) => word.wordEnglish === wordEnglish
  );

  const status: WordTaskStatus = {
    english: "pending",
    telugu: "pending",
    drawing: "pending",
  };

  const mergeStatus = (
    current: WordTaskProgressState,
    next: WordTaskProgressState
  ): WordTaskProgressState => {
    if (current === "correct" || next === "correct") return "correct";
    if (current === "wrong" || next === "wrong") return "wrong";
    return "pending";
  };

  const toProgressState = (
    row: DashboardLearningRow | null
  ): WordTaskProgressState => {
    if (!row || row.skip) return "pending";
    return row.correct ? "correct" : "wrong";
  };

  for (const attempt of wordRecord?.attempts ?? []) {
    status.english = mergeStatus(
      status.english,
      toProgressState(
        toDashboardRow(
          studentId,
          wordEnglish,
          attempt.date,
          0,
          "audio_english",
          attempt.spoken_en,
          "E"
        )
      )
    );
    status.telugu = mergeStatus(
      status.telugu,
      toProgressState(
        toDashboardRow(
          studentId,
          wordEnglish,
          attempt.date,
          0,
          "audio_telugu",
          attempt.spoken_te,
          "E"
        )
      )
    );
    status.drawing = mergeStatus(
      status.drawing,
      toProgressState(
        toDashboardRow(
          studentId,
          wordEnglish,
          attempt.date,
          0,
          "image",
          attempt.drawn,
          "E"
        )
      )
    );
  }

  return status;
}

export async function clearStudentLearningProgress(studentId: number) {
  await deleteStudentLearningByStudentId(studentId);
}

type StoredSchool = {
  schoolId: number;
  schoolName?: string;
  teachers?: HardcodedTeacher[];
  class?: Record<number, { classId: number; sections: Record<string, StudentRecord[]> }>;
};

const stripTrailingNumberSuffix = (studentName: string): string =>
  studentName.replace(/\s+\d+$/, "").trim();

const normalizeStudentNamesInSchoolStore = async (): Promise<void> => {
  await openDB();

  const schools = (await getData<StoredSchool[]>("school")) ?? [];

  for (const school of schools) {
    if (!school.class) continue;

    let schoolChanged = false;

    for (const classEntry of Object.values(school.class)) {
      for (const sectionStudents of Object.values(classEntry.sections ?? {})) {
        for (const student of sectionStudents ?? []) {
          const normalizedName = stripTrailingNumberSuffix(student.studentName);
          if (normalizedName && normalizedName !== student.studentName) {
            student.studentName = normalizedName;
            schoolChanged = true;
          }
        }
      }
    }

    if (schoolChanged) {
      await saveData(school, "school");
    }
  }
};

type StudentRecordWithSection = StudentRecord & { section: SectionName };

const getStudentsFromSchoolStore = async (): Promise<StudentRecordWithSection[]> => {
  await openDB();
  const activeSchoolId = getActiveSchoolId();
  const schools = (await getData<StoredSchool[]>("school")) ?? [];
  const activeSchool = schools.find((school) => school.schoolId === activeSchoolId);
  const classMap = activeSchool?.class;
  if (!classMap) return [];

  return Object.values(classMap).flatMap((classEntry) =>
    Object.entries(classEntry.sections ?? {}).flatMap(([section, sectionStudents]) => {
      const normalizedSection =
        SECTION_NAMES.find((entry) => entry === section) ?? "A";
      return (sectionStudents ?? []).map((student) => ({
        ...student,
        section: normalizedSection,
      }));
    })
  );
};

export async function repairActiveSchoolRosterIfEmpty(): Promise<boolean> {
  const activeSchoolStudents = await getStudentsFromSchoolStore();
  if (activeSchoolStudents.length > 0) {
    return false;
  }

  await seedActiveSchoolToIndexedDB(getActiveSchoolId());
  return true;
}

export async function getStudentsForActiveSchoolFromStore(): Promise<HardcodedStudent[]> {
  const students = await getStudentsFromSchoolStore();
  if (students.length > 0) {
    return students.map((student) => ({
      studentId: student.studentId,
      studentName: student.studentName,
      classId: student.classId,
      section: student.section,
      rollNo: student.rollNo,
      schoolId: student.schoolId,
      colour: student.colour,
      emoji: student.emoji,
    }));
  }

  return getStudentsForActiveSchool();
}

export type AddStudentPayload = {
  classId: number;
  section: "A" | "B" | "C" | "D" | "E";
  studentName: string;
  rollNo: string;
};

export async function addStudentToActiveSchool(
  payload: AddStudentPayload
): Promise<{ ok: boolean; message: string; student?: StudentRecord }> {
  await openDB();

  const activeSchoolId = getActiveSchoolId();
  const schools = (await getData<StoredSchool[]>("school")) ?? [];
  const activeSchool = schools.find((school) => school.schoolId === activeSchoolId);

  if (!activeSchool) {
    return { ok: false, message: "Active school was not found." };
  }

  if (payload.classId < 1 || payload.classId > 5) {
    return { ok: false, message: "Class must be between 1 and 5." };
  }

  const studentName = payload.studentName.trim();
  const rollNo = payload.rollNo.trim();
  if (!studentName) {
    return { ok: false, message: "Student name is required." };
  }
  if (!/^\d+$/.test(rollNo) || Number(rollNo) < 1) {
    return { ok: false, message: "Roll number must be a positive whole number." };
  }

  const section = payload.section;
  const classMap = activeSchool.class ?? {};
  const classEntry = classMap[payload.classId] ?? {
    classId: payload.classId,
    sections: { A: [], B: [], C: [], D: [], E: [] },
  };

  const sections = {
    A: classEntry.sections?.A ?? [],
    B: classEntry.sections?.B ?? [],
    C: classEntry.sections?.C ?? [],
    D: classEntry.sections?.D ?? [],
    E: classEntry.sections?.E ?? [],
  };

  const studentsInSchool = Object.values(classMap).flatMap((entry) =>
    Object.values(entry.sections ?? {}).flatMap((sectionStudents) =>
      sectionStudents ?? []
    )
  );

  const studentsInSection = sections[section] ?? [];
  if (studentsInSection.some((student) => student.rollNo === rollNo)) {
    return {
      ok: false,
      message: `Section ${section} in class ${payload.classId} already has a student with roll number ${rollNo}.`,
    };
  }

  const sectionCode = SECTION_NAMES.indexOf(section) + 1;
  const studentId =
    activeSchoolId * 100000 +
    payload.classId * 10000 +
    sectionCode * 1000 +
    Number(rollNo);
  if (studentsInSchool.some((student) => student.studentId === studentId)) {
    return {
      ok: false,
      message: `Student ID conflict for class ${payload.classId}, section ${section}, roll number ${rollNo}.`,
    };
  }

  const colourIndex = studentsInSchool.length % COLOURS.length;
  const student: StudentRecord = {
    studentId,
    studentName,
    classId: payload.classId,
    rollNo,
    schoolId: activeSchoolId,
    colour: COLOURS[colourIndex],
    emoji: EMOJIS[colourIndex],
  };

  const updatedClassEntry = {
    classId: classEntry.classId,
    sections: {
      ...sections,
      [section]: [...sections[section], student],
    },
  };

  const updatedSchool: StoredSchool = {
    ...activeSchool,
    class: {
      ...classMap,
      [payload.classId]: updatedClassEntry,
    },
  };

  await saveData(updatedSchool, "school");

  const schoolDataEntry = SCHOOL_DATA.find((entry) => entry.school.schoolId === activeSchoolId);
  if (schoolDataEntry) {
    schoolDataEntry.class = updatedSchool.class as ClassSectionMap;
  }

  return {
    ok: true,
    message: "Student added successfully.",
    student,
  };
}

/**
 * Returns the full `StudentRecord` for a student.
 * Returns `null` if the student is not found.
 */
export async function getStudentProgress(studentId: number): Promise<StudentRecord | null> {
  const all = await getStudentsFromSchoolStore();
  return all.find((s) => s.studentId === studentId) ?? null;
}

export async function getTeacherDashboardData(): Promise<TeacherDashboardData> {
  await openDB();
  const preferredLocalLanguage = getPreferredLocalLanguage();

  const students = await getStudentsFromSchoolStore();
  const studentMap = new Map(students.map((s) => [s.studentId, s]));
  const studentIds = new Set(students.map((s) => s.studentId));

  const words = (await getData<Word[]>("word")) ?? [];
  const wordMap = new Map(words.map((w) => [w.english, w]));

  const studentLearningRecords =
    ((await getData<StudentLearningRecord[]>("student_learning")) ?? []).filter(
      (row) => studentIds.has(row.studentId)
    );
  const learningRecordByStudent = new Map(
    studentLearningRecords.map((record) => [record.studentId, record])
  );
  const learningRows = flattenLearningRows(studentLearningRecords, wordMap);

  const studentsOverview: TeacherStudentOverview[] = students
    .map((student) => {
      const learningRecord = learningRecordByStudent.get(student.studentId);

      let wordsLearned = 0;
      let englishCorrect = 0;
      let teluguCorrect = 0;
      let drawingCorrect = 0;
      let pronunciationCorrect = 0;
      let pronunciationAttempted = 0;
      let wordsShown = 0;
      let wordsAttempted = 0;
      let wordsDrawn = 0;
      let lastPracticeMs: number | null = null;

      for (const word of learningRecord?.words ?? []) {
        let wordWasShown = false;
        let hasPronunciationAttempt = false;
        let hasPronunciationCorrect = false;
        let hasEnglishCorrect = false;
        let hasTeluguCorrect = false;
        let hasEnglishAttempt = false;
        let hasTeluguAttempt = false;
        let hasDrawingAttempt = false;
        let hasDrawingCorrect = false;

        for (const attempt of word.attempts ?? []) {
          const ts = toTimestampMs(attempt.date);
          if (ts && (!lastPracticeMs || ts > lastPracticeMs)) {
            lastPracticeMs = ts;
          }

          const spokenEn = normalizeAttemptStatus(attempt.spoken_en);
          const spokenTe = normalizeAttemptStatus(attempt.spoken_te);
          const drawn = normalizeAttemptStatus(attempt.drawn);

          const hasStatusSignal =
            spokenEn !== "not attempted" ||
            spokenTe !== "not attempted" ||
            drawn !== "not attempted";
          if (hasStatusSignal || attempt.count > 0) {
            wordWasShown = true;
          }

          if (isAttemptCompleted(spokenEn) || isAttemptCompleted(spokenTe)) {
            hasPronunciationAttempt = true;
          }
          if (isAttemptCompleted(spokenEn)) {
            hasEnglishAttempt = true;
          }
          if (isAttemptCompleted(spokenTe)) {
            hasTeluguAttempt = true;
          }
          if (isAttemptCompleted(drawn)) {
            hasDrawingAttempt = true;
          }

          if (spokenEn === "correct") {
            hasEnglishCorrect = true;
            hasPronunciationCorrect = true;
          }
          if (spokenTe === "correct") {
            hasTeluguCorrect = true;
            hasPronunciationCorrect = true;
          }
          if (drawn === "correct") {
            hasDrawingCorrect = true;
          }
        }

        if (wordWasShown) wordsShown += 1;
        if (hasPronunciationAttempt || hasDrawingAttempt) wordsAttempted += 1;
        if (hasPronunciationAttempt) pronunciationAttempted += 1;
        if (hasPronunciationCorrect) pronunciationCorrect += 1;
        if (hasEnglishCorrect) englishCorrect += 1;
        if (hasTeluguCorrect) teluguCorrect += 1;
        if (hasDrawingAttempt) wordsDrawn += 1;
        if (hasDrawingCorrect) drawingCorrect += 1;
        if (hasEnglishAttempt && hasTeluguAttempt && hasDrawingAttempt) {
          wordsLearned += 1;
        }
      }

      const wordsSkipped = Math.max(0, wordsShown - wordsAttempted);

      return {
        id: student.studentId,
        name: student.studentName,
        rollNo: student.rollNo,
        class: student.classId,
        section: student.section,
        englishCorrect,
        teluguCorrect,
        drawnCorrect: drawingCorrect,
        easy: englishCorrect,
        medium: teluguCorrect,
        hard: drawingCorrect,
        pronunciationCorrect,
        pronunciationAttempted,
        wordsShown,
        wordsAttempted,
        wordsSkipped,
        wordsSpoken: pronunciationAttempted,
        wordsLearned,
        wordsDrawn,
        lastPractice: formatDate(lastPracticeMs),
      };
    })
    .sort((a, b) => a.class - b.class || a.name.localeCompare(b.name));

  const skipRequests: TeacherSkipRequest[] = learningRows
    .filter((row) => row.skip)
    .map((row, idx) => {
      const word = wordMap.get(row.wordEnglish);
      const studentName = studentMap.get(row.studentId)?.studentName ?? "Unknown";
      const ts = toTimestampMs(row.timestamp);
      return {
        id: row.id || `${row.studentId}-${idx}`,
        studentId: row.studentId,
        classId: studentMap.get(row.studentId)?.classId ?? 0,
        studentName,
        wordEnglish: row.wordEnglish,
        wordLocal: resolveWordLocalText(word, preferredLocalLanguage),
        category: word?.category ?? "-",
        level: word?.grade ? String(word.grade) : undefined,
        status: "pending" as const,
        timestamp: formatDateTime(ts),
        teacherNote: null,
      };
    })
    .sort((a, b) => {
      const aTs = Date.parse(a.timestamp);
      const bTs = Date.parse(b.timestamp);
      const safeA = Number.isNaN(aTs) ? 0 : aTs;
      const safeB = Number.isNaN(bTs) ? 0 : bTs;
      return safeB - safeA;
    });

  return {
    stats: {
      totalStudents: students.length,
      wordsSpoken: studentsOverview.reduce(
        (total, student) => total + student.pronunciationAttempted,
        0
      ),
      wordsLearned: studentsOverview.reduce(
        (total, student) => total + student.wordsLearned,
        0
      ),
    },
    students: studentsOverview,
    skipRequests,
    wrongSubmissions: [],
  };
}

export async function getLeaderboardData(classId?: number): Promise<LeaderboardData> {
  const dashboard = await getTeacherDashboardData();

  const classFilteredStudents =
    typeof classId === "number"
      ? dashboard.students.filter((student) => student.class === classId)
      : dashboard.students;

  const sorted = classFilteredStudents
    .map((student) => {
      const words = student.wordsLearned;
      const collectibles =
        student.wordsLearned * 2 + student.wordsSpoken + student.wordsDrawn;
      return {
        id: student.id,
        name: student.name,
        class: student.class,
        words,
        collectibles,
      };
    })
    .sort(
      (a, b) =>
        b.collectibles - a.collectibles ||
        b.words - a.words ||
        a.name.localeCompare(b.name)
    );

  let currentRank = 0;
  let lastScore: { collectibles: number; words: number } | null = null;

  const entries: LeaderboardEntry[] = sorted.map((student, idx) => {
    if (
      !lastScore ||
      lastScore.collectibles !== student.collectibles ||
      lastScore.words !== student.words
    ) {
      currentRank = idx + 1;
      lastScore = {
        collectibles: student.collectibles,
        words: student.words,
      };
    }

    return {
      ...student,
      rank: currentRank,
    };
  });

  return { entries };
}

export async function initialSetup() {
  if (_initialSetupInFlight) {
    await _initialSetupInFlight;
    return;
  }

  const ensureWordsSeeded = async (): Promise<void> => {
    const existingWords = (await getData<Word[]>("word")) ?? [];
    if (existingWords.length > 0) {
      return;
    }

    const first100Words =
      "https://storage.googleapis.com/vocab-assets/word-lists/first_v4.csv";

    let words: Word[];
    try {
      words = await fetchAndParseCsv(first100Words);
      console.log(words);
    } catch (error) {
      console.error(`Failed to fetch word list: ${error}`);
      return;
    }

    try {
      await storeWords(words);
      console.log("Saved words to database.");
    } catch (error) {
      console.log(`Error while storing words: ${error}`);
    }
  };

  const runSetup = async (): Promise<void> => {
    const runCommonSetup = async (): Promise<void> => {
      await backupSchoolStoreOnce();
      await ensureSchoolSeededIfMissing(getActiveSchoolId());
      await normalizeStudentNamesInSchoolStore();
      await ensureWordsSeeded();
      await ensureRequiredModelCache();
      await cleanupOldQwenOnnxCache();
    };

    const lastDbVersion = Number(localStorage.getItem("dbVersion") || "0");
    if (lastDbVersion >= DB_VERSION) {
      await runCommonSetup();
      return;
    }

    try {
      await openDB();
      console.log("Database initialized successfully.");
      await runCommonSetup();
    } catch (error) {
      console.error(`Failed to initialize DB setup: ${error}`);
    }
  };

  _initialSetupInFlight = runSetup();
  _setupReady = _initialSetupInFlight;

  try {
    await _initialSetupInFlight;
  } finally {
    _initialSetupInFlight = null;
  }

  // Tests

  // test getNextWords

  // const nextWords = await getNextWords(1);
  // console.log(nextWords);

  // test audio english endpoint

  // const audioEngURL: Response = await fetch(
  //   "https://storage.googleapis.com/vocab-images/test-audio/microwave.wav",
  // );
  // const audioEng = await audioEngURL.blob();
  // console.log("Audio English fetched successfully:", audioEng);
  // const submission: Submission = {
  //   studentId: 1,
  //   wordEnglish: "microwave",
  //   difficulty: "E",
  //   type: "audio_english",
  //   skip: false,
  //   file: audioEng,
  //   correct: null,
  // };
  // const correct = await submit(submission);
  // console.log("Submission result:", correct);

  // test image endpoint

  // const imageURL: Response = await fetch(
  //   "https://storage.googleapis.com/vocab-images/variants/airplane/57.png",
  // );
  // const image = await imageURL.blob();
  // const submission: Submission = {
  //   studentId: 1,
  //   wordEnglish: "airplane",
  //   difficulty: "E",
  //   type: "image",
  //   skip: false,
  //   file: image,
  //   correct: null,
  // };
  // const correct = await submit(submission);
  // console.log("Submission result:", correct);

  // const nextWords = await getAllStudentProfiles();
  // console.log(nextWords);
}
